import { useRef, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { PanelLeft } from 'lucide-react';
import { cn } from '@clarix/ui';
import type { RailItem } from '@clarix/shared-types';
import {
  PANEL_DEFAULT_WIDTH,
  PANEL_MAX_WIDTH,
  PANEL_MIN_WIDTH,
  useSidebar,
} from './context/SidebarContext';

const KEYBOARD_RESIZE_STEP = 16;

/** Drag handle on the pinned panel's right edge; double-click resets the width. */
function PanelResizeHandle() {
  const { panelWidth, setPanelWidth, isResizing, setIsResizing } = useSidebar();
  const dragStart = useRef<{ x: number; width: number } | null>(null);

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStart.current = { x: e.clientX, width: panelWidth };
    setIsResizing(true);
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    setPanelWidth(dragStart.current.width + e.clientX - dragStart.current.x);
  };

  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    dragStart.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setIsResizing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const next: Partial<Record<string, number>> = {
      ArrowLeft: panelWidth - KEYBOARD_RESIZE_STEP,
      ArrowRight: panelWidth + KEYBOARD_RESIZE_STEP,
      Home: PANEL_MIN_WIDTH,
      End: PANEL_MAX_WIDTH,
    };
    const width = next[e.key];
    if (width === undefined) return;
    e.preventDefault();
    setPanelWidth(width);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar panel"
      aria-valuenow={panelWidth}
      aria-valuemin={PANEL_MIN_WIDTH}
      aria-valuemax={PANEL_MAX_WIDTH}
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={() => setPanelWidth(PANEL_DEFAULT_WIDTH)}
      onKeyDown={handleKeyDown}
      className="group absolute -right-1.5 top-0 z-10 flex h-full w-3 cursor-col-resize touch-none justify-center outline-none"
    >
      <span
        className={cn(
          'group-hover:bg-primary/40 group-focus-visible:bg-primary h-full w-0.5 bg-transparent transition-colors',
          isResizing && 'bg-primary',
        )}
      />
    </div>
  );
}

/**
 * Panel titles for apps whose panels are still hand-written components rather
 * than object-driven configs. An app that ships `AppDefinition.sidebar` declares
 * its titles on each `PanelConfig` and the host passes them in as `panelTitles`,
 * which wins over this map — so every entry here disappears as its app migrates.
 * Do NOT add to it: put the title on the panel config instead.
 */
const LEGACY_PANEL_TITLES: Record<string, string> = {
  home: 'Quick Access',
  // CRM remote panels (rail labels "Comm"/"Comp" are abbreviated; spell them out).
  // There is no `crm` panel: the CRM entities are flat rail icons with no panel.
  'crm-operations': 'Operations',
  'crm-commission': 'Commission',
  'crm-reports': 'Reports',
  // There is no `crm-data` panel: the objects/fields "Config" rail is retired —
  // CRM Configuration is a row in the Settings panel (/crm/settings/crm-configuration)
  // and its object list is that page's own tab switcher.
  // Master config + the Settings group live under one "Settings" rail entry.
  'crm-master': 'Settings',
  // Payroll remote: the rail entry is "Payroll", but this panel is specifically
  // the pay-period timeline (Upcoming / In progress / Completed), so it needs its
  // own name rather than the rail-label fallback.
  'payroll-pay-history': 'Run Payroll',
};

/** panelId → header text, supplied by the host from its sidebar configs. */
export type PanelTitles = Readonly<Record<string, string>>;

/**
 * Resolution order: the app's own config → the legacy map → the rail item's
 * label → the raw id (which only surfaces for a panel nothing declares, and
 * reads as the bug it is).
 */
export function panelTitle(
  panelId: string,
  items: readonly RailItem[],
  titles?: PanelTitles,
): string {
  return (
    titles?.[panelId] ??
    LEGACY_PANEL_TITLES[panelId] ??
    items.find((i) => i.panelId === panelId)?.label ??
    panelId
  );
}

export function PanelHeader({ title, onCollapse }: { title: string; onCollapse?: () => void }) {
  return (
    // h-12 matches the TopBar, so the title shares that row. The collapse control
    // is in the side detail itself; the TopBar keeps one for after the panel closes.
    <div className="border-border flex h-12 shrink-0 items-center gap-1.5 border-b px-3">
      {onCollapse && (
        <button
          type="button"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          onClick={onCollapse}
          aria-label="Collapse sidebar"
          aria-expanded
        >
          <PanelLeft className="h-4 w-4" strokeWidth={1.5} />
        </button>
      )}
      {/* `text-foreground`, not a raw gray: it must match the token page titles use. */}
      <span className="text-foreground truncate text-2xl font-semibold">{title}</span>
    </div>
  );
}

interface SidebarPanelProps {
  items: readonly RailItem[];
  renderPanelContent?: (panelId: string, onNavigate: () => void) => ReactNode;
  /** Header titles declared by the active app's sidebar config. */
  panelTitles?: PanelTitles;
}

/**
 * Pinned panel (opened by clicking a rail item). User-resizable via its right
 * edge; full height beside the rail — the TopBar starts after it. Body is
 * supplied by the shell.
 */
export function SidebarPanel({ items, renderPanelContent, panelTitles }: SidebarPanelProps) {
  const { activePanel, isPanelOpen, cancelHoverClose, scheduleHoverClose } = useSidebar();

  if (!isPanelOpen || !activePanel) return null;
  // clario-frontend parity: clicking a link inside the PINNED panel does NOT
  // close it — the panel stays open until the user collapses it explicitly.
  const body = renderPanelContent?.(activePanel, () => {});
  if (!body) return null;

  return (
    <aside
      onMouseEnter={cancelHoverClose}
      onMouseLeave={scheduleHoverClose}
      className={cn(
        'border-border fixed left-16 top-0 z-30 hidden h-screen w-[var(--sidebar-panel-w)] flex-col border-r bg-white lg:flex',
        'animate-in slide-in-from-left-2 duration-150',
      )}
    >
      <PanelResizeHandle />
      <PanelHeader title={panelTitle(activePanel, items, panelTitles)} />
      <div className="scrollbar-hide flex-1 overflow-y-auto">{body}</div>
    </aside>
  );
}
